import format from 'date-fns/format'
import amapi from '@/api/amapi'
import router from '@/router/index'
import { responseError } from '@/helpers/helpers'

const initialState = () => ({
  profile: null,
  approvedCommissions: 0,
  cpaValue: 0,
  campaigns: [],
  linkAccountFailed: false,
  canCreateProfile: false,
  paymentHistory: [],
  statistics: {
    campaignTracking: [],
    clickBreakdown: [],
    acquisitionDetails: [],
    trafficDetails: []
  },
  lastLoaded: '',
  reportingDate: new Date().toISOString().substr(0, 7),
  loadingStatistics: false,
})

const state = initialState

const getters = {
  profile: state => state.profile,
  campaigns: state => state.campaigns,
  linkAccountFailed: state => state.linkAccountFailed,
  canCreateProfile: state => state.canCreateProfile,
  paymentHistory: state => state.paymentHistory,
  monthlyStatistics: state => ({
    ...state.statistics
  }),
  yearlyStatistics: state => ({
    ...state.statistics,
    acquisitionDetails: undefined
  }),
  lifetimeStatistics: state => ({
    ...state.statistics,
    acquisitionDetails: undefined
  }),
  trafficDetails: state => state.statistics.trafficDetails,
  reportingDate: state => state.reportingDate,
  loadingStatistics: state => state.loadingStatistics,
  overview: state => {
    const earnings = state.statistics.acquisitionDetails.reduce((carry, item) => {
      carry += item.col5
      return carry
    }, 0)
    // payout is avg cpa value
    const payout = state.statistics.acquisitionDetails.reduce((carry, item) => {
      carry += item.col5
      return carry
    }, 0) / state.statistics.acquisitionDetails.length
    return {
      signUps: state.statistics.acquisitionDetails.length,
      payout,
      earnings
    }
  }
}

const mutations = {
  SET_PROFILE (state, profile) {
    state.profile = profile
  },
  SET_APPROVED_COMMISSIONS (state, approvedCommissions) {
    state.approvedCommissions = approvedCommissions
  },
  SET_CPA (state, cpa) {
    state.cpaValue = cpa
  },
  SET_CAMPAIGNS (state, payload) {
    state.campaigns = payload.campaigns.map(item => ({
      created_at: format(new Date(item.date_created.date), 'yyyy-MM-dd HH:mm:ss'),
      code: item.code,
      description: item.description,
      id: item.id
    }))
  },
  SET_LINK_ACCOUNT_FAILED (state, status) {
    state.linkAccountFailed = status
  },
  SET_CAN_CREATE_PROFILE (state, canCreate) {
    state.canCreateProfile = canCreate
  },
  SET_PAYMENT_HISTORY (state, history) {
    if (history.iTotalRecords !== 0) {
      state.paymentHistory = history.aaData.map(item => ({
        month: item[0],
        status: item[1],
        method: item[2]
      }))
    }
  },
  SET_CAMPAIGN_TRACKING (state, stats) {
    state.statistics.campaignTracking = stats?.campaign_tracking.iTotalRecords > 0
      ? stats.campaign_tracking.aaData.map(stat => ({
        col1: stat[0],
        col2: stat[1],
        col3: stat[2],
        col4: stat[3],
        col5: stat[4],
        col6: stat[5],
        col7: stat[6],
        col8: stat[7],
        col9: stat[8],
      }))
      : []
  },
  SET_ACQUISITION_DETAILS (state, stats) {
    state.statistics.acquisitionDetails = stats?.acquisition_details.iTotalRecords > 0
      ? stats.acquisition_details.aaData.map(stat => ({
        col1: stat[0],
        col2: stat[1],
        col3: stat[2],
        col4: stat[3],
        col5: Number.parseFloat(stat[4]),
        col6: stat[5],
      }))
      : []
  },
  SET_CLICK_BREAKDOWN (state, stats) {
    state.statistics.clickBreakdown = stats?.click_breakdown.iTotalRecords > 0 ? stats.click_breakdown.aaData : []
  },
  SET_REPORTING_DATE (state, date) {
    state.reportingDate = date
  },
  SET_LOADING_STATISTICS (state, loading) {
    state.loadingStatistics = loading
  },
  SET_LAST_LOADED (state, last) {
    state.lastLoaded = last
  },
  SET_TRAFFIC_DETAILS (state, details) {
    state.statistics.trafficDetails = details.map(item => ({
      timestamp: format(new Date(item.date.date), 'yyyy-MM-dd HH:mm:ss'),
      campaign: item.campaign,
      ipaddress: item.ipaddress,
      referer: item.referer,
      browser: item.browser,
    }))
  }
}

const actions = {
  async getProfile ({ commit, dispatch }) {
    const response = await amapi.makecall('aff_profile', {}, true)
    if (response.result === 'error' && response.match_emails) {
      // call aff_link_account
      dispatch('linkAccount')
    } else if (response.result === 'error') {
      // if error and match_emails = false  then  goto create new profile page
      commit('SET_CAN_CREATE_PROFILE', true)
      await router.push({ name: 'affiliates_no_active_account' })
    } else {
      const error = responseError(response)
      if (error) {
        commit('SET_LINK_ACCOUNT_FAILED', true)
        throw error
      }
      commit('SET_CAN_CREATE_PROFILE', false)
      commit('SET_PROFILE', response?.user)
      commit('SET_APPROVED_COMMISSIONS', response?.approved_commissions)
      commit('SET_CPA', response?.cpa_value)
    }
  },
  async linkAccount ({ commit, dispatch }) {
    try {
      await amapi.makecall('aff_link_account')
      dispatch('getProfile')
    } catch (e) {
      commit('SET_LINK_ACCOUNT_FAILED', true)
      await router.push({
        name: 'multi-accounts'
      })
      throw e
    }
  },
  async paymentHistory ({ commit }) {
    try {
      const { aaData, iTotalLength } = await amapi.makecall('aff_payment_history')

      commit('SET_PAYMENT_HISTORY', { aaData, iTotalLength })
    } catch (e) {
      throw e
    }
  },
  async setPaypalAddress ({ commit, dispatch }, paypal_address) {
    try {
      const { resultmsg } = await amapi.makecall('aff_profile', { paypal_address })
      await dispatch('getProfile')

      return resultmsg
    } catch (e) {
      throw e
    }
  },
  async setClassification ({ commit, dispatch }, classification) {
    try {
      await amapi.makecall('aff_profile', { classification })
      await dispatch('getProfile')
    } catch (e) {
      throw e
    }
  },
  async affCampaigns ({ commit }, data) {
    try {
      const response = await amapi.makecall('aff_campaigns', { ...data })

      if (data.action === 'campaign_codes') {
        commit('SET_CAMPAIGNS', response)
      }

      return response
    } catch (e) {
      throw e
    }
  },
  async getMonthlyStatistics ({ commit, state }, forceReload = false) {
    if (!forceReload && state.lastLoaded === 'monthly') {
      // do not load data if it already loaded
      return
    }
    let response
    commit('SET_LOADING_STATISTICS', true)
    try {
      response = await amapi.makecall('aff_monthly_statistics', { affiliate_date: state.reportingDate })
    } catch (e) {
      commit('SET_LOADING_STATISTICS', false)
      throw e
    }
    commit('SET_ACQUISITION_DETAILS', response)
    commit('SET_CLICK_BREAKDOWN', response)
    commit('SET_CAMPAIGN_TRACKING', response)
    commit('SET_LAST_LOADED', 'monthly')
    commit('SET_LOADING_STATISTICS', false)
  },
  async getYearlyStatistics ({ commit, state }, forceReload = false) {
    if (!forceReload && state.lastLoaded === 'yearly') {
      // do not load data if it already loaded
      return
    }
    let response
    commit('SET_LOADING_STATISTICS', true)
    const affiliate_date = format(new Date(state.reportingDate), 'yyyy')
    try {
      response = await amapi.makecall('aff_yearly_statistics', { affiliate_date })
    } catch (e) {
      commit('SET_LOADING_STATISTICS', false)
      throw e
    }
    commit('SET_CLICK_BREAKDOWN', response)
    commit('SET_CAMPAIGN_TRACKING', response)
    commit('SET_LAST_LOADED', 'yearly')
    commit('SET_LOADING_STATISTICS', false)
  },
  async getLifetimeStatistics ({ commit, state }, forceReload = false) {
    if (!forceReload && state.lastLoaded === 'lifetime') {
      // do not load data if it already loaded
      return
    }
    let response
    commit('SET_LOADING_STATISTICS', true)
    try {
      response = await amapi.makecall('aff_lifetime_statistics')
    } catch (e) {
      commit('SET_LOADING_STATISTICS', false)
      throw e
    }
    commit('SET_CLICK_BREAKDOWN', response)
    commit('SET_CAMPAIGN_TRACKING', response)
    commit('SET_LAST_LOADED', 'lifetime')
    commit('SET_LOADING_STATISTICS', false)
  },
  async getTrafficDetails ({ commit }, date) {
    try {
      const { traffic_details } = await amapi.makecall('aff_traffic_details', { date })
      commit('SET_TRAFFIC_DETAILS', traffic_details)
    } catch (e) {
      throw e
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
